import React, { useState, useEffect } from 'react';
import { DialogueTurn, Participant, DialogueOption, EmotionAnalysis } from '../../types';
import { 
  HandThumbUpIcon, 
  HandThumbDownIcon, 
  EllipsisVerticalIcon,
  SpeakerWaveIcon,
  HeartIcon
} from '@heroicons/react/24/outline';
import { Menu, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import DialogueOptionsModal from './DialogueOptionsModal';
import EmotionAnalysisModal from './EmotionAnalysisModal';
import apiService from '../../services/api';

interface DialogueBubbleProps {
  turn: DialogueTurn;
  participants: Participant[];
  turnIndex: number;
  caseId: string;
  onModifyDialogue: (action: string, turnIndex: number, originalText: string, customText?: string, selectedOption?: string) => void;
  onPlayAudio?: (turnIndex: number) => void;
  isPlaying?: boolean;
  isAnalyzed?: boolean;
  emotionAnalysis?: EmotionAnalysis | null;
  totalTurns?: number; // Total number of turns in dialogue
}

const DialogueBubble: React.FC<DialogueBubbleProps> = ({
  turn,
  participants,
  turnIndex,
  caseId,
  onModifyDialogue,
  onPlayAudio,
  isPlaying = false,
  isAnalyzed = false,
  emotionAnalysis: preAnalyzedEmotion = null,
  totalTurns = 0,
}) => {
  // Calculate how many turns are available after this one
  // turnIndex is 1-based turnNumber, so remaining turns = totalTurns - turnIndex
  const remainingTurns = totalTurns - turnIndex;
  const maxFollowingTurns = Math.min(4, remainingTurns); // Max 4, or fewer if less available
  // Helper function to detect medical professionals - MUST match backend logic exactly
  const isMedicalProfessional = (speaker: string): boolean => {
    // Use participant data for robust detection (same as backend)
    const participant = participants.find((p: any) => {
      const pName = (p.name || '').trim();
      const speakerTrimmed = speaker.trim();
      return pName === speakerTrimmed || pName.toLowerCase() === speakerTrimmed.toLowerCase();
    });
    
    // If found in participants, check their role
    if (participant && participant.role) {
      const roleLower = participant.role.toLowerCase();
      
      // Medical professional roles
      const medicalRoles = [
        'doctor', 'physician', 'nurse', 'clinician', 'attending', 'resident',
        'medical student', 'intern', 'fellow', 'therapist', 'counselor',
        'social worker', 'chaplain', 'oncologist', 'cardiologist', 'neurologist',
        'pediatrician', 'surgeon', 'psychiatrist', 'psychologist', 'specialist',
        'consultant', 'healthcare provider', 'medical professional', 'care team member'
      ];
      
      if (medicalRoles.some(role => roleLower.includes(role))) {
        return true;
      }
      
      // Family/patient roles
      const familyRoles = [
        'mother', 'father', 'parent', 'guardian', 'sibling', 'brother', 'sister',
        'grandmother', 'grandfather', 'grandparent', 'aunt', 'uncle', 'family',
        'patient', 'child', 'son', 'daughter', 'spouse', 'partner', 'friend',
        'relative', 'caregiver', 'legal guardian'
      ];
      
      if (familyRoles.some(role => roleLower.includes(role))) {
        return false;
      }
    }
    
    // Fallback: Use name pattern matching (specific only)
    const medicalTitleRegex = /^(dr\.?\s|doctor\s|prof\.?\s|professor\s)/i;
    if (medicalTitleRegex.test(speaker)) {
      return true;
    }
    
    const medicalCredentialRegex = /(,?\s+(md|do|dmd|dds|dvm|phd|pharmd|dpt|rn|lpn|cna|pa-c|np|crna)\.?)$/i;
    if (medicalCredentialRegex.test(speaker)) {
      return true;
    }
    
    const familyIndicatorRegex = /\((mother|father|parent|guardian|brother|sister|sibling|grandmother|grandfather|grandparent|aunt|uncle|friend|patient|child|son|daughter|spouse|partner|wife|husband|family member|relative|caregiver)\)/i;
    if (familyIndicatorRegex.test(speaker)) {
      return false;
    }
    
    // Default to family/patient (safer to show button than hide it)
    return false;
  };
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(turn.line);
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [optionsAction, setOptionsAction] = useState<string>('');
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [dialogueOptions, setDialogueOptions] = useState<DialogueOption[]>([]);
  const [showEmotionModal, setShowEmotionModal] = useState(false);
  const [emotionAnalysis, setEmotionAnalysis] = useState<EmotionAnalysis | null>(null);
  const [isLoadingEmotion, setIsLoadingEmotion] = useState(false);
  const [dialogueVote, setDialogueVote] = useState<string | null>(null);
  const [isVoting, setIsVoting] = useState(false);
  
  // Multi-turn regeneration state
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [followingTurnsCount, setFollowingTurnsCount] = useState(0);
  const [optionsError, setOptionsError] = useState<string | null>(null);

  // Update emotionAnalysis state if preAnalyzedEmotion changes (e.g., after bulk analysis completes)
  useEffect(() => {
    setEmotionAnalysis(preAnalyzedEmotion);
  }, [preAnalyzedEmotion]);

  // Clear local emotion analysis when turn text changes (indicating dialogue was modified)
  useEffect(() => {
    // Clear emotion analysis when text changes to ensure fresh analysis
    setEmotionAnalysis(null);
  }, [turn.line]);

  // Clear local emotion analysis when isAnalyzed becomes false (indicating cache was cleared)
  useEffect(() => {
    if (!isAnalyzed) {
      setEmotionAnalysis(null);
    }
  }, [isAnalyzed]);

  // Load dialogue votes when component mounts
  useEffect(() => {
    loadDialogueVotes();
  }, [caseId, turnIndex]);

  // Clear dialogue vote when turn text changes (indicating dialogue was modified)
  useEffect(() => {
    setDialogueVote(null);
  }, [turn.line]);

  const participant = participants.find(p => p.name === turn.speaker || p.role === turn.speaker);
  const speakerName = participant ? participant.name : turn.speaker;
  
  // Color scheme for different speakers - consistent per speaker
  const speakerColors = [
    { bg: 'bg-blue-100', text: 'text-blue-800' },
    { bg: 'bg-slate-100', text: 'text-slate-800' },
    { bg: 'bg-emerald-100', text: 'text-emerald-800' },
    { bg: 'bg-rose-100', text: 'text-rose-800' },
    { bg: 'bg-purple-100', text: 'text-purple-800' },
    { bg: 'bg-amber-100', text: 'text-amber-800' },
    { bg: 'bg-cyan-100', text: 'text-cyan-800' },
    { bg: 'bg-orange-100', text: 'text-orange-800' },
    { bg: 'bg-lime-100', text: 'text-lime-800' },
    { bg: 'bg-pink-100', text: 'text-pink-800' },
  ];

  // Get consistent color index based on speaker name, not turn index
  const getSpeakerColorIndex = (speaker: string): number => {
    // Create a more robust hash from the speaker name for consistent color assignment
    let hash = 0;
    for (let i = 0; i < speaker.length; i++) {
      const char = speaker.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    // Add additional entropy by including the speaker length and first/last characters
    hash = hash + speaker.length * 31;
    if (speaker.length > 0) {
      hash = hash + speaker.charCodeAt(0) * 17;
      hash = hash + speaker.charCodeAt(speaker.length - 1) * 13;
    }
    
    return Math.abs(hash) % speakerColors.length;
  };

  // Get colors based on speaker (consistent)
  const colorIndex = getSpeakerColorIndex(speakerName);
  const colors = speakerColors[colorIndex];

  // Get alignment based on speaker role (medical professional = left, patient/family = right)
  const isDoctor = isMedicalProfessional(turn.speaker);
  const alignment = isDoctor
    ? { align: 'items-start', bubbleAlign: 'rounded-bl-none' }  // Doctor on LEFT
    : { align: 'items-end', bubbleAlign: 'rounded-br-none' };   // Patient/Family on RIGHT

  const handleSaveEdit = () => {
    onModifyDialogue('custom', turnIndex, turn.line, editText);
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditText(turn.line);
    setIsEditing(false);
  };

  const handleShowOptions = async (action: string) => {
    setOptionsError(null); // Clear any previous errors
    
    // For regenerate, show modal to select how many following turns to regenerate
    if (action === 'regenerate') {
      setShowRegenerateModal(true);
      return;
    }
    
    setOptionsAction(action);
    setShowOptionsModal(true);
    setIsLoadingOptions(true);
    
    try {
      // Get the case ID from the current URL or context
      const caseId = window.location.pathname.split('/').pop();
      if (!caseId) {
        throw new Error('Case ID not found');
      }
      
      console.log('Getting options for:', { caseId, action, originalText: turn.line, turnIndex });
      
      const response = await apiService.getDialogueModificationOptions(
        caseId,
        action,
        turn.line,
        turnIndex
      );
      
      console.log('Received options:', response);
      setDialogueOptions(response.options);
    } catch (error: any) {
      console.error('Failed to get dialogue options:', error);
      // Show error instead of falling back to direct modification
      const errorMsg = error.response?.status === 504 
        ? 'Request timed out. The AI is taking too long to respond. Please try again.'
        : error.response?.data?.error || 'Failed to generate options. Please try again.';
      setOptionsError(errorMsg);
      setShowOptionsModal(false);
    } finally {
      setIsLoadingOptions(false);
    }
  };
  
  const handleConfirmRegenerate = async () => {
    setOptionsError(null); // Clear any previous errors
    setShowRegenerateModal(false);
    setOptionsAction('regenerate');
    setShowOptionsModal(true);
    setIsLoadingOptions(true);
    
    try {
      const caseId = window.location.pathname.split('/').pop();
      if (!caseId) {
        throw new Error('Case ID not found');
      }
      
      console.log('Getting regenerate options for:', { 
        caseId, 
        action: 'regenerate', 
        originalText: turn.line, 
        turnIndex,
        followingTurnsCount
      });
      
      const response = await apiService.getDialogueModificationOptions(
        caseId,
        'regenerate',
        turn.line,
        turnIndex,
        followingTurnsCount
      );
      
      console.log('Received regenerate options:', response);
      setDialogueOptions(response.options);
    } catch (error: any) {
      console.error('Failed to get dialogue options:', error);
      // Show error instead of falling back to direct modification
      const errorMsg = error.response?.status === 504 
        ? 'Request timed out. The AI is taking too long to respond. Please try again.'
        : error.response?.data?.error || 'Failed to generate options. Please try again.';
      setOptionsError(errorMsg);
      setShowOptionsModal(false);
    } finally {
      setIsLoadingOptions(false);
    }
  };

  const handleSelectOption = (option: DialogueOption) => {
    console.log('Selected option:', { 
      action: optionsAction, 
      turnIndex, 
      originalText: turn.line, 
      selectedOption: option.text,
      isMultiTurn: !!option.turns 
    });
    
    // For multi-turn options, pass the turns array as JSON
    // For single-turn options, pass the text
    const selectedData = option.turns && option.turns.length > 1
      ? JSON.stringify(option.turns)
      : option.text;
    
    // Use the new API with selected option
    onModifyDialogue(optionsAction, turnIndex, turn.line, undefined, selectedData);
    setShowOptionsModal(false);
  };

  const handleCloseOptionsModal = () => {
    setShowOptionsModal(false);
    setDialogueOptions([]);
    setIsLoadingOptions(false);
  };

  const handleAnalyzeEmotion = async () => {
    // If we already have pre-analyzed emotion data, use it
    if (preAnalyzedEmotion) {
      setEmotionAnalysis(preAnalyzedEmotion);
      setShowEmotionModal(true);
      return;
    }

    // If we already have the analysis from individual analysis, just show the modal
    if (emotionAnalysis) {
      setShowEmotionModal(true);
      return;
    }

    try {
      setIsLoadingEmotion(true);
      setShowEmotionModal(true);
      
      console.log('Analyzing emotion for:', { caseId, turnIndex, speaker: turn.speaker, text: turn.line });
      
      const analysis = await apiService.analyzeEmotion(caseId, turnIndex);
      setEmotionAnalysis(analysis);
      
      console.log('Emotion analysis completed:', analysis);
      
      // Force refresh of emotion votes and user reports after analysis
      // This ensures the modal shows fresh data for the current text
      setTimeout(() => {
        console.log('Refreshing emotion data after analysis completion');
        // The modal will automatically refresh when it opens due to the useEffect watching for text changes
      }, 100);
      
    } catch (error) {
      console.error('Failed to analyze emotion:', error);
      // Show modal with error message instead of null
      setEmotionAnalysis({
        assessment: "Emotion analysis failed",
        emotions: {
          gratitude: 0,
          sadness: 0,
          anger: 0,
          fear: 0
        },
        overall: {
          emotionalIntensity: 0,
          emotionalComplexity: 0,
          communicationEffectiveness: 0
        },
        reasoning: "Failed to analyze emotions. This may be due to API limitations or network issues. Please try again."
      });
    } finally {
      setIsLoadingEmotion(false);
    }
  };

  const handleCloseEmotionModal = () => {
    setShowEmotionModal(false);
    // Keep emotionAnalysis data so it can be reopened
  };

  const loadDialogueVotes = async () => {
    try {
      const response = await apiService.getDialogueVotes(caseId, turnIndex);
      if (response.success) {
        setDialogueVote(response.userVote);
      }
    } catch (error) {
      console.error('Failed to load dialogue votes:', error);
    }
  };

  const handleVoteDialogue = async (vote: 'up' | 'down' | 'neutral') => {
    if (isVoting) return;
    
    setIsVoting(true);
    try {
      const response = await apiService.voteOnDialogue({
        caseId,
        turnIndex,
        vote
      });
      
      if (response.success) {
        setDialogueVote(vote);
      }
    } catch (error) {
      console.error('Failed to vote on dialogue:', error);
    } finally {
      setIsVoting(false);
    }
  };

  const getVoteButtonColor = (voteType: 'up' | 'down') => {
    if (dialogueVote === voteType) {
      return voteType === 'up' ? 'text-green-600 bg-green-100' : 'text-red-600 bg-red-100';
    }
    return 'text-gray-400 hover:text-gray-600';
  };

  return (
    <div className={`flex flex-col ${alignment.align} mb-4`}>
      {/* Error message banner */}
      {optionsError && (
        <div className="mb-2 bg-red-50 border border-red-200 rounded-lg p-3 max-w-4xl">
          <div className="flex items-start gap-2">
            <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <p className="text-sm text-red-800 font-medium">{optionsError}</p>
            </div>
            <button
              onClick={() => setOptionsError(null)}
              className="text-red-400 hover:text-red-600 transition-colors"
              title="Dismiss"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}
      <div className={`dialogue-bubble ${colors.bg} ${alignment.bubbleAlign} max-w-4xl px-4 py-3 relative group`}>
        <p className={`font-bold text-sm mb-1 ${colors.text}`}>
          {speakerName}
        </p>
        
        {isEditing ? (
          <div className="space-y-3">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <label className="block text-xs font-medium text-blue-700 mb-2">
                Editing dialogue for {speakerName}
              </label>
              <textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                className="w-full p-3 border border-blue-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 resize-y min-h-[120px] text-sm leading-relaxed"
                rows={6}
                placeholder="Edit the dialogue text here..."
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleCancelEdit}
                className="text-sm font-semibold text-gray-600 hover:text-gray-800 px-4 py-2 rounded-md hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-md transition-colors"
              >
                Save Changes
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-start justify-between">
            <p className="text-gray-900 flex-1">{turn.line}</p>
            {emotionAnalysis && !isMedicalProfessional(turn.speaker) && (
              <button
                onClick={() => setShowEmotionModal(true)}
                className="ml-2 p-1 rounded-full bg-blue-100 hover:bg-blue-200 transition-colors"
                title="View emotion analysis"
              >
                <svg className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </button>
            )}
          </div>
        )}

        {/* Vote buttons - shown on hover */}
        <div className="absolute bottom-2 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <button
            onClick={() => handleVoteDialogue('up')}
            disabled={isVoting}
            className={`p-1 rounded-full bg-white shadow-sm transition-colors ${getVoteButtonColor('up')}`}
            title="Vote up - Good dialogue"
          >
            <HandThumbUpIcon className="h-4 w-4" />
          </button>
          
          <button
            onClick={() => handleVoteDialogue('down')}
            disabled={isVoting}
            className={`p-1 rounded-full bg-white shadow-sm transition-colors ${getVoteButtonColor('down')}`}
            title="Vote down - Poor dialogue"
          >
            <HandThumbDownIcon className="h-4 w-4" />
          </button>

          {onPlayAudio && (
            <button
              onClick={() => onPlayAudio(turnIndex)}
              className={`p-1 rounded-full bg-white shadow-sm hover:bg-gray-50 transition-colors ${
                isPlaying ? 'text-blue-600' : 'text-gray-500 hover:text-blue-500'
              }`}
              title="Play Audio"
            >
              <SpeakerWaveIcon className="h-4 w-4" />
            </button>
          )}

          <Menu as="div" className="relative">
            <Menu.Button className="p-1 rounded-full bg-white shadow-sm hover:bg-gray-50 transition-colors">
              <EllipsisVerticalIcon className="h-4 w-4 text-gray-500 hover:text-blue-500" />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 bottom-full mb-2 w-32 origin-bottom-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                <div className="py-1">
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={() => handleShowOptions('rephrase')}
                        className={`${
                          active ? 'bg-gray-100' : ''
                        } block w-full text-left px-4 py-2 text-sm text-gray-700`}
                      >
                        Rephrase
                      </button>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={() => handleShowOptions('regenerate')}
                        className={`${
                          active ? 'bg-gray-100' : ''
                        } block w-full text-left px-4 py-2 text-sm text-gray-700`}
                      >
                        Regenerate
                      </button>
                    )}
                  </Menu.Item>
                  {/* Only show Analyze Emotion button for patient/family members */}
                  {!isMedicalProfessional(turn.speaker) && (
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={handleAnalyzeEmotion}
                          className={`${
                            active ? 'bg-gray-100' : ''
                          } block w-full text-left px-4 py-2 text-sm flex items-center ${
                            isAnalyzed ? 'text-green-700 bg-green-50' : 'text-gray-700'
                          }`}
                        >
                          <svg className={`h-4 w-4 mr-2 ${isAnalyzed ? 'text-green-600' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                          </svg>
                          {isAnalyzed ? '✓ Emotion Analyzed' : 'Analyze Emotion'}
                        </button>
                      )}
                    </Menu.Item>
                  )}
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={() => setIsEditing(true)}
                        className={`${
                          active ? 'bg-gray-100' : ''
                        } block w-full text-left px-4 py-2 text-sm text-gray-700`}
                      >
                        Custom Edit
                      </button>
                    )}
                  </Menu.Item>
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>

      {/* Regenerate Count Selection Modal */}
      {showRegenerateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold mb-4">Regenerate Dialogue Turns</h3>
            <p className="text-sm text-gray-600 mb-4">
              Choose how many following turns to regenerate after this one:
            </p>
            
            {maxFollowingTurns === 0 ? (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-sm text-yellow-800">
                  This is the last turn in the dialogue. Only this turn can be regenerated.
                </p>
              </div>
            ) : (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Regenerate: This turn + next <span className="text-blue-600 font-bold">{followingTurnsCount}</span> turn{followingTurnsCount !== 1 ? 's' : ''}
                  <span className="text-gray-500 ml-2">
                    ({followingTurnsCount + 1} total)
                  </span>
                </label>
                <input
                  type="range"
                  min="0"
                  max={maxFollowingTurns}
                  value={Math.min(followingTurnsCount, maxFollowingTurns)}
                  onChange={(e) => setFollowingTurnsCount(parseInt(e.target.value))}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>0 (This turn only)</span>
                  {maxFollowingTurns >= 2 && <span>{Math.floor(maxFollowingTurns / 2)}</span>}
                  <span>{maxFollowingTurns} (Max available)</span>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {remainingTurns} turn{remainingTurns !== 1 ? 's' : ''} remaining after this one
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setShowRegenerateModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRegenerate}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Generate Options
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Options Modal */}
      <DialogueOptionsModal
        isOpen={showOptionsModal}
        onClose={handleCloseOptionsModal}
        onSelectOption={handleSelectOption}
        originalText={turn.line}
        action={optionsAction}
        options={dialogueOptions}
        isLoading={isLoadingOptions}
        followingTurnsCount={followingTurnsCount}
      />

      {/* Emotion Analysis Modal */}
      <EmotionAnalysisModal
        key={`${caseId}-${turnIndex}-${turn.line}`} // Force remount when text changes
        isOpen={showEmotionModal}
        onClose={handleCloseEmotionModal}
        emotionAnalysis={emotionAnalysis}
        isLoading={isLoadingEmotion}
        speaker={turn.speaker}
        text={turn.line}
        caseId={caseId}
        turnIndex={turnIndex}
      />
    </div>
  );
};

export default DialogueBubble;
